import React, { useState, useEffect } from "react";
import { dataService } from "../../../../../Services/data/data.service";
import { Box } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import Button from "../../../../Shared/Buttons/Button";
import Input from "../../../../Shared/Input";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../../Shared/Select";
import { Plus, Search } from "lucide-react";
import { crudTypes, PriceDefinitionFilterTypes } from "../../../../../Config/constants";
import PriceDefinitionTable from "../PriceDefinition/PriceDefinitionTable";
import PriceDefinitionDialog from "../PriceDefinition/PriceDefinitionDialog";
import TablePagination from "@mui/material/TablePagination";

function PriceDefinitionTab(props) {
  const classes = useStyles();

  // General
  const [originalPriceDefinitions, setOriginalPriceDefinitions] = useState([]); // Store the original list of price definitions - unfiltered
  const [priceDefinitionList, setPriceDefinitionList] = useState([]); // Filtered and sorted
  const [filters, setFilters] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Price Definition Dialog
  const [selectedPriceDefinition, setSelectedPriceDefinition] = useState(null);
  const [priceDefinitionDialogOpen, setPriceDefinitionDialogOpen] = useState(false);
  const [crudType, setCrudType] = useState(null); // Create, Edit, View

  // Fetch Price Definitions
  useEffect(() => {
    dataService
      .getAllPriceDefinitionsWithEntries(props.Login.userid)
      .then((response) => {
        setOriginalPriceDefinitions(response);
        setPriceDefinitionList(response);
      })
      .catch((error) => {
        console.error("Error fetching price definitions:", error);
        // Handle error, e.g., show a message to the user
      });
  }, [props.Login.userid]);

  // Fetch Companies
  useEffect(() => {
    if (props.Login && props.Login.userid) {
      dataService
        .getAllCompaniesByUserId(props.Login.userid)
        .then((response) => {
          setAllCompanies(response);
        })
        .catch((error) => {
          console.error("Error fetching companies:", error);
        });
    }
  }, [props.Login.userid]);

  // Filter Logic
  const getFilterValue = (type) => {
    let filter = filters.find((x) => x.type === type);

    if (filter) {
      return filter.filterValue;
    } else {
      return "";
    }
  };

  const updateFilters = (type, event) => {
    let tempFilters = [...filters];
    if (tempFilters.some((x) => x.type === type)) {
      let index = tempFilters.indexOf(tempFilters.find((x) => x.type === type));
      tempFilters.splice(index, 1);
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    } else {
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    }
    setFilters(tempFilters);
    filterList(tempFilters);
    setPage(0); // Reset to first page when filters change
  };

  const updateCompanyFilter = (selectedCompany) => {
    let tempFilters = [...filters];
    if (tempFilters.some((x) => x.type === PriceDefinitionFilterTypes.COMPANY)) {
      let index = tempFilters.indexOf(tempFilters.find((x) => x.type === PriceDefinitionFilterTypes.COMPANY));
      tempFilters.splice(index, 1);
      if (selectedCompany) {
        tempFilters.unshift({ type: PriceDefinitionFilterTypes.COMPANY, filterValue: selectedCompany.id });
      }
    } else {
      if (selectedCompany) {
        tempFilters.unshift({ type: PriceDefinitionFilterTypes.COMPANY, filterValue: selectedCompany.id });
      }
    }
    setFilters(tempFilters);
    filterList(tempFilters);
    setPage(0); // Reset to first page when filters change
  };

  const filterList = (thisFilters, priceDefinitions) => {
    let varFilteredlist = priceDefinitions ? priceDefinitions : [...originalPriceDefinitions];

    for (let i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case PriceDefinitionFilterTypes.SEARCH:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.code.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceDefinitionFilterTypes.NAME:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceDefinitionFilterTypes.CODE:
          varFilteredlist = varFilteredlist.filter((x) => x.code.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceDefinitionFilterTypes.COMPANY:
          varFilteredlist = varFilteredlist.filter((x) => x.companyId === filter.filterValue);
          break;
        default:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.code.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
      }
    }
    
    // Sort alphabetically by code
    varFilteredlist = varFilteredlist.sort((a, b) => a.code.localeCompare(b.code));
    
    if (priceDefinitions) {
      return varFilteredlist;
    } else {
      setPriceDefinitionList(varFilteredlist);
    }
  };

  // Create Price Definition Logic
  const openCreatePriceDefinitionDialog = () => {
    // Logic to open the dialog for creating a new price definition
    setCrudType(crudTypes.CREATE);
    setPriceDefinitionDialogOpen(true);
  };

  const closePriceDefinitionDialog = () => {
    // Logic to close the dialog for creating a new price definition
    setPriceDefinitionDialogOpen(false);
    setCrudType(null);
  };

  const handleEditDefinition = (priceDefinitionId) => {
    // Logic to open the dialog for editing an existing price definition
    const priceDefinition = originalPriceDefinitions.find((x) => x.id === priceDefinitionId);
    if (!priceDefinition) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceDefinition(priceDefinition);
    setCrudType(crudTypes.UPDATE);
    setPriceDefinitionDialogOpen(true);
  };

  const handleViewDefinition = (priceDefinitionId) => {
    // Logic to open the dialog for viewing an existing price definition

    const priceDefinition = originalPriceDefinitions.find((x) => x.id === priceDefinitionId);
    if (!priceDefinition) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceDefinition(priceDefinition);
    setCrudType(crudTypes.READ);
    setPriceDefinitionDialogOpen(true);
  };

  // Add this handler to add a new definition
  const handleAddPriceDefinition = (newDefinition) => {
    const sortAlphabetically = (definitions) => {
      return [...definitions].sort((a, b) => a.code.localeCompare(b.code));
    };

    setOriginalPriceDefinitions((prev) => sortAlphabetically([...prev, newDefinition]));
    
    // Apply current filters to the new list including the new definition
    const newOriginalList = [...originalPriceDefinitions, newDefinition];
    const filteredList = filterList(filters, newOriginalList);
    setPriceDefinitionList(sortAlphabetically(filteredList || newOriginalList));
  };

  // Add this handler to update an existing definition
  const handleUpdatePriceDefinition = (updatedDefinition) => {
    const sortAlphabetically = (definitions) => {
      return [...definitions].sort((a, b) => a.code.localeCompare(b.code));
    };

    setOriginalPriceDefinitions((prev) => sortAlphabetically(prev.map((def) => (def.id === updatedDefinition.id ? updatedDefinition : def))));
    
    // Apply current filters to the updated list
    const newOriginalList = originalPriceDefinitions.map((def) => (def.id === updatedDefinition.id ? updatedDefinition : def));
    const filteredList = filterList(filters, newOriginalList);
    setPriceDefinitionList(sortAlphabetically(filteredList || newOriginalList));
  };

  // Utility function to get search value
  const getSearchValue = () => {
    return getFilterValue(PriceDefinitionFilterTypes.SEARCH);
  };

  // Utility function to get selected company
  const getSelectedCompany = () => {
    const companyId = getFilterValue(PriceDefinitionFilterTypes.COMPANY);
    return allCompanies.find((company) => company.id === companyId) || null;
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated rows
  const paginatedPriceDefinitions = priceDefinitionList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <React.Fragment>
      <div className={classes.tableManagementContainer}>
        <div className={classes.tableManagementContainer__start}>
          <div className={classes.searchContainer}>
            <input
              placeholder="Search by name or code"
              className={classes.search__input}
              type="text"
              value={getFilterValue(PriceDefinitionFilterTypes.SEARCH)}
              onChange={(e) => updateFilters(PriceDefinitionFilterTypes.SEARCH, e)}
            />
            <Search size={16} className={classes.inputIcon} />
          </div>
          <div className={classes.filterContainer}>
            <Select value={getSelectedCompany()?.id} onChange={updateCompanyFilter} size="small">
              <SelectTrigger>{getSelectedCompany()?.name || "All Companies"}</SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>All Companies</SelectItem>
                {allCompanies.map((company) => (
                  <SelectItem key={company.id} value={company}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className={classes.tableManagementContainer__end}>
          <Button size="small" startIcon={<Plus />} onClick={() => openCreatePriceDefinitionDialog()}>
            Add Price Definition
          </Button>
        </div>
      </div>
      {/* Table */}
      <PriceDefinitionTable priceDefinitions={paginatedPriceDefinitions} handleEditDefinition={handleEditDefinition} handleViewDefinition={handleViewDefinition} searchValue={getSearchValue()} />

      {/* Pagination */}
      <TablePagination
        component="div"
        count={priceDefinitionList.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Dialog */}
      <PriceDefinitionDialog
        dialogOpen={priceDefinitionDialogOpen}
        closeDialog={closePriceDefinitionDialog}
        crudType={crudType}
        priceDefinition={selectedPriceDefinition}
        onAdd={handleAddPriceDefinition}
        onUpdate={handleUpdatePriceDefinition}
        {...props}
      />
    </React.Fragment>
  );
}

export default PriceDefinitionTab;

const useStyles = makeStyles({
  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid #C3996B",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },
});
